import {
  OnDeckLogo,
  Caret,
  FormattedMessageDiv,
  HamburgerBar,
  CloseIcon,
} from '@odmonorepo/core-components';
import { useMutation } from '@tanstack/react-query';
import { Link, useRouter } from '@tanstack/react-router';
import { useParties, getSalesAgentInfo } from 'merchant-api/spiceroad';
import { queryClient, unifiedApiClient } from 'merchant-api';
import { SalesAgentInfoCard } from '../../../Features/SalesAgentInfo/Cards/SalesAgentInfoCard';
import { CustomerServiceInfoCard } from '../../../Features/SalesAgentInfo/Cards/CustomerServiceInfoCard';
import { useState } from 'react';
import {
  Button,
  Card,
  Collapse,
  ListGroup,
  Offcanvas,
  Row,
  Stack
} from 'react-bootstrap';
import { useIntl } from 'react-intl';

export function SideNav() {
  const [show, setShow] = useState(false);
  const router = useRouter();
  const { hasLOC, hasTL, partiesData, isErrorParties, isLoadingParties } = useParties();
  const { salesAgentInfo, isSalesAgentInfoLoading } = getSalesAgentInfo();
  const isFundedCustomer = !!(hasLOC || hasTL);
  const { mutate: logout } = useMutation({
    throwOnError: false,
    mutationFn: () => unifiedApiClient.POST('/public/token/logout'),
    onSuccess: () => {
      // Drop the whole query cache, not just the signed-in gate: any cached query (current
      // application, offers, loan state) would otherwise survive into the next sign-in and
      // render the previous user's data for up to a staleTime.
      queryClient.clear();
      router.navigate({ to: '/' });
    },
    onSettled: () => router.invalidate({sync: true})
  });

  return (
    <>
      <Card className="d-lg-none d-flex flex-row p-2 rounded-0 shadow-none sticky-top">
        <HamburgerBar
          role="button"
          className="d-flex"
          onClick={() => setShow(true)}
        />
        <Link to="/">
          <OnDeckLogo className="ms-5 mb-2"/>
        </Link>
      </Card>
      <Offcanvas show={show} onHide={() => setShow(false)} responsive="lg" className='w-auto'>
        <Card
          className="rounded-0 vh-100 shadow-none pt-5 pb-3 bg-light overflow-y-auto overflow-x-hidden sticky-top"
          style={{ width: '272px' }}
        >
          <Stack>
            {show && (
              <CloseIcon
                role="button"
                onClick={() => setShow(false)}
                className="d-flex justify-content-end me-4"
              />
            )}
            <Link to="/"><OnDeckLogo className="ms-5 mb-8"/></Link>
            <Collapse in={(!isLoadingParties && !isErrorParties)}>
              <div>
                <div className="ms-5 fw-bold lh-sm">
                  {partiesData?.user?.name}
                </div>
                <div className="fs-7 ms-5 lh-sm">
                  {partiesData?.parties[0]?.businesses[0]?.legalName}
                </div>
                <Link
                  className="fs-7 ms-5 mt-3 td text-decoration-none lh-sm"
                  to='/profile'
                >
                  Edit Profile
                </Link>
              </div>
            </Collapse>
            <ListGroup className="rounded-0 my-4 border-top border-bottom border-light-subtle" variant="flush">
              <LinkItem to="/">Dashboard</LinkItem>
              {hasLOC && <LinkItem to="/drawfunds">Draw Funds</LinkItem>}
              <LinkItem to="/payment">Make a Payment</LinkItem>
              <LinkItem to="/documents">Documents</LinkItem>
              {!isSalesAgentInfoLoading && !salesAgentInfo && !isFundedCustomer &&
              <ListItem><ContactUs/></ListItem>}
            </ListGroup>
          </Stack>
          <Row className='text-start' >
            {isFundedCustomer && salesAgentInfo && (
              <FormattedMessageDiv className="fs-7 text-gray ms-5 mt-4" id="salesAgentInfo.additionalCapital.heading" />
            )}
            <SalesAgentInfoCard/>
            {isFundedCustomer && (
              <>
                <FormattedMessageDiv className="fs-7 text-gray ms-5 mt-4" id="customerService.heading" />
                <CustomerServiceInfoCard className='border-bottom border-light-medium-gray'/>
              </>
            )}
            <Button className="fw-bold mt-2" style={{width:'150px'}} variant='link' onClick={() => logout()}>Sign Out</Button>
          </Row>
        </Card>
      </Offcanvas>
    </>
  );
}

const ListItem: typeof ListGroup.Item = (props) => (
  <ListGroup.Item variant="light" role="button" className='px-5 py-4'>
    {props.children}
  </ListGroup.Item>
)

const LinkItem: typeof Link = (props) => (
  <ListItem>
    <Link className='fw-normal text-body' activeProps={{className: "fw-bold"}} {...props} />
  </ListItem>
)

export const ContactUs = () => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden">
      <Stack
        direction="horizontal"
        onClick={() => setOpen((o) => !o)}
        role="button"
      >
        Contact Us
        <Caret className="ms-auto me-2" direction={open ? 'up' : 'down'} />
      </Stack>
      <Stack
        className="fs-7"
        gap={1}
        style={{ height: open ? '90px' : '0px', transition: 'all 500ms' }}
      >
        <a
          className="text-decoration-none mt-3"
          href={`tel: ${intl.formatMessage({
            id: 'contactInfo.phone.helpline',
          })}`}
        >
          <FormattedMessageDiv id="contactInfo.phone.helpline" />
        </a>
        <a
          className="text-decoration-none"
          href={`mailto: ${intl.formatMessage({
            id: 'common.customerServiceEmail',
          })}`}
        >
          <FormattedMessageDiv id="common.customerServiceEmail" />
        </a>
        <FormattedMessageDiv id="contactInfo.businessHours" />
      </Stack>
    </div>
  );
};

export default SideNav;
